'use client';

import { useState } from 'react';

interface AppHeroVideoProps {
  src: string;
  alt?: string;
  className?: string;
  priority?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  poster?: string;
  [key: string]: unknown;
}

function AppHeroVideo({
  src,
  className = '',
  priority,
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  poster,
  ...props
}: AppHeroVideoProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const commonClassName = `${className} ${isLoading ? 'bg-gray-200' : ''}`;

  if (hasError) {
    return (
      <div className={`${commonClassName} flex items-center justify-center bg-muted`}>
        <p className="text-muted-foreground">Video unavailable</p>
      </div>
    );
  }

  // Filter out non-video props to avoid React warnings
  const { alt, ...videoProps } = props;

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 animate-pulse" />
        </div>
      )}
      <video
        src={src}
        poster={poster}
        className={commonClassName}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline={playsInline}
        onError={handleError}
        onLoadedData={handleLoad}
        {...videoProps}
      >
        Your browser does not support the video tag.
      </video>
      <div className='absolute inset-0 bg-black/30'></div>
    </div>
  );
}

export default AppHeroVideo;

